import { useCallback, useState } from 'react';
import { positions, transitions, useAlert } from 'react-alert';
import { gql, useMutation } from '@apollo/client';
import { InitialStateType as AuthStateType } from '@context/AuthContext/reducer';
import { errorMessage } from '@firsttable/functions';
import { useIsDesktop } from '@firsttable/web-theme';
import { cn } from '@discover-lib/utils';
import SignInModal, { type SignInModalProps } from '@/components/organisms/Modal/SignInModal';
import { useModal } from '@/components/context/ModalContext';
import { useAuthState } from '@/components/context/AuthContext';
import FavouriteAlertMessage from './AlertMessage';
import FavouriteIcon from './FavouriteIcon';

const MEMBER_ADD_FAVOURITE = gql`
  mutation memberAddFavourite($restaurantId: Int) {
    memberAddFavourite(restaurantId: $restaurantId) {
      id
      email
      favouriteIds
    }
  }
`;

const MEMBER_REMOVE_FAVOURITE = gql`
  mutation memberRemoveFavourite($restaurantId: Int) {
    memberRemoveFavourite(restaurantId: $restaurantId) {
      id
      email
      favouriteIds
    }
  }
`;

export type FavouriteProps = {
  restaurant: Prettify<SignInModalProps['restaurant'] & { id: number }>;
  width?: number;
  height?: number;
  className?: string;
};

const Favourite = ({ restaurant, width, height, className }: FavouriteProps) => {
  const [localState, setLocalState] = useState<boolean | null>(null);
  const { user, updateMember } = useAuthState();
  const [add, { error: addError }] = useMutation(MEMBER_ADD_FAVOURITE);
  const [remove, { error: removeError }] = useMutation(MEMBER_REMOVE_FAVOURITE);
  const alert = useAlert();
  const isDesktop = useIsDesktop();
  const isProcessing = localState !== null;

  const { showModal } = useModal();

  const active = localState ?? user?.favouriteIds?.some((i) => i === restaurant.id);

  const onClick = useCallback(
    (e, force) => {
      if (localState !== null) return;

      setLocalState(!active);

      const handleLogin = () =>
        showModal(<SignInModal restaurant={restaurant} />, {
          type: 'signInForm',
          closeIconColor: 'grayscale.600',
          onRequestClose: (close, state: Partial<AuthStateType> = {}) => {
            if (state?.user) {
              onClick(e, true);
              setTimeout(() => close(), 200);
            } else {
              setLocalState(null);
              close();
            }
          },
        });

      if (!force && !user) {
        return handleLogin();
      }

      const action = active ? remove : add;
      const error = active ? removeError : addError;
      action({
        variables: {
          restaurantId: restaurant.id,
        },
      })
        .then(({ data }) => {
          updateMember(Object.values(data)[0]);
          if (error) {
            throw error;
          }
          alert.show(<FavouriteAlertMessage active={active} />, {
            timeout: 8000,
            // @ts-expect-error `alert.show` doesn't support props for the custom alert template like `type`, `position` and `transition`
            type: 'primary',
            position: isDesktop ? positions.BOTTOM_RIGHT : positions.TOP_CENTER,
            transition: transitions.FADE,
            closeIconStyle: {
              style: {
                display: 'none',
              },
            },
          });
        })
        .catch((error) => {
          if (error.message.includes('not authenticated')) {
            return handleLogin();
          }
          alert.error(errorMessage(error), {
            timeout: 5000,
          });
        })
        .finally(() => {
          setLocalState(null);
        });
    },
    [
      active,
      add,
      addError,
      alert,
      remove,
      removeError,
      restaurant,
      showModal,
      updateMember,
      user,
      localState,
      isDesktop,
    ]
  );

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick(e, false);
      }}
      style={{ cursor: isProcessing ? 'default' : 'pointer' }}
      className={cn(
        '-mt-1 size-5 min-h-5 min-w-5 hover:opacity-80 lg:size-7 lg:min-h-7 lg:min-w-7',
        isProcessing && 'animate-pulse',
        className
      )}
      title={active ? 'Remove from favourites' : 'Add to favourites'}>
      <FavouriteIcon active={active} width={width} height={height} className="text-brand-red" />
    </button>
  );
};

export default Favourite;
